<!-- =========================================================================================
  File Name: DynamicFilterPanel.vue
  Description: Generic and reusable dynamic filter panel component with Basic/Advanced modes
  ----------------------------------------------------------------------------------------
  Author: Serviroutes Team
  Version: 2025-10-27 - V2 (API Compatible with array values)
========================================================================================== -->

<template>
	<div class="dynamic-filter-panel">
		<vx-card class="mb-0">
			<!-- Header with Mode Switch -->
			<template slot="no-body">
				<div class="filter-header">
					<div class="header-content">
						<h5 class="filter-title">{{ title || $t("FiltersAdvanced.Title") }}</h5>
						<div class="mode-toggle">
							<span class="mode-label">{{ $t("FiltersAdvanced.BasicMode") }}</span>
							<vs-switch v-model="isAdvancedMode" />
							<span class="mode-label">{{ $t("FiltersAdvanced.AdvancedMode") }}</span>
						</div>
					</div>
					<feather-icon
						v-if="showCloseButton"
						icon="XIcon"
						svgClasses="w-5 h-5 cursor-pointer"
						@click="$emit('close')"
					/>
				</div>
			</template>

			<div class="filter-content">
				<!-- BASIC MODE -->
				<div v-if="!isAdvancedMode" class="basic-mode">
					<div class="mode-description">
						<h6>{{ $t("FiltersAdvanced.BasicModeTitle") }}</h6>
						<p>{{ $t("FiltersAdvanced.BasicModeDescription") }}</p>
					</div>

					<div class="basic-filter-row">
						<div class="filter-field">
							<label>{{ $t("FiltersAdvanced.SearchBy") }}</label>
							<vs-select
								v-model="basicFilter.field"
								class="w-full"
								:placeholder="$t('FiltersAdvanced.SelectField')"
							>
								<vs-select-item
									v-for="field in availableFields"
									:key="field.value"
									:value="field.value"
									:text="field.label"
								/>
							</vs-select>
						</div>

						<div class="filter-value">
							<label>{{ $t("FiltersAdvanced.Value") }}</label>
							<div class="value-input-wrapper">
								<vs-input
									v-model="basicFilter.value"
									:placeholder="$t('FiltersAdvanced.EnterValue')"
									class="flex-1"
									@keyup.enter="applyBasicFilter"
								/>
								<vs-button
									v-if="basicFilter.value"
									type="flat"
									color="danger"
									icon-pack="feather"
									icon="icon-x"
									size="small"
									@click="clearBasicValue"
								/>
							</div>
						</div>
					</div>

					<div class="basic-actions">
						<vs-button color="primary" icon-pack="feather" icon="icon-search" @click="applyBasicFilter">
							{{ $t("FiltersAdvanced.Search") }}
						</vs-button>
						<vs-button type="border" icon-pack="feather" icon="icon-trash-2" @click="clearFilters">
							{{ $t("FiltersAdvanced.ClearFilters") }}
						</vs-button>
					</div>
				</div>

				<!-- ADVANCED MODE -->
				<div v-else class="advanced-mode">
					<div class="mode-description">
						<h6>{{ $t("FiltersAdvanced.AdvancedModeTitle") }}</h6>
						<p>{{ $t("FiltersAdvanced.AdvancedModeDescription") }}</p>
					</div>

					<!-- Field Selection -->
					<div class="field-selection">
						<label>{{ $t("FiltersAdvanced.FieldSelection") }}</label>
						<div class="selection-row">
							<vs-select
								v-model="selectedFieldToAdd"
								class="flex-1"
								:placeholder="$t('FiltersAdvanced.SelectFilterToAdd')"
							>
								<vs-select-item
									v-for="field in availableFieldsToAdd"
									:key="field.value"
									:value="field.value"
									:text="field.label"
								/>
							</vs-select>
							<vs-button
								color="primary"
								icon-pack="feather"
								icon="icon-plus"
								:disabled="!selectedFieldToAdd"
								@click="addFilterFromDropdown"
							>
								{{ $t("FiltersAdvanced.Add") }}
							</vs-button>
							<vs-button
								color="danger"
								type="border"
								icon-pack="feather"
								icon="icon-trash-2"
								@click="clearFilters"
							>
								{{ $t("FiltersAdvanced.Clear") }}
							</vs-button>
						</div>
					</div>

					<!-- Active Filters -->
					<div class="active-filters-section">
						<label>{{ $t("FiltersAdvanced.ActiveFilters") }}:</label>

						<!-- Filter Items with Multiple Values (Chips) -->
						<transition-group name="filter-list" tag="div" class="filters-container">
							<div v-for="filter in filtersList" :key="filter.id" class="filter-item-advanced">
								<div class="filter-item-header">
									<span class="filter-label">{{ getFieldLabel(filter.field) }}</span>
									<div class="filter-actions">
										<vs-button
											v-if="showFiltersConfig"
											type="flat"
											size="small"
											icon-pack="feather"
											icon="icon-settings"
											@click="toggleFilterConfig(filter.id)"
										/>
										<vs-button
											type="flat"
											color="danger"
											size="small"
											icon-pack="feather"
											icon="icon-trash-2"
											@click="removeFilter(filter.id)"
										/>
									</div>
								</div>

								<!-- Values as Chips -->
								<div class="filter-values">
									<div class="chips-container">
										<transition-group name="chip" tag="div" class="chips-wrapper">
											<div
												v-for="(value, index) in filter.values"
												:key="`${filter.id}-${index}`"
												class="chip"
											>
												<span class="chip-text">{{ value }}</span>
												<feather-icon
													icon="XIcon"
													svgClasses="w-3 h-3 cursor-pointer"
													@click="removeValue(filter.id, index)"
												/>
											</div>
										</transition-group>
									</div>

									<!-- Add Value Input -->
									<div class="add-value-input">
										<vs-input
											v-model="filter.newValue"
											:placeholder="$t('FiltersAdvanced.AddValue')"
											size="small"
											@keyup.enter="addValue(filter.id)"
										/>
										<vs-button
											color="primary"
											type="flat"
											size="small"
											icon-pack="feather"
											icon="icon-plus"
											:disabled="!filter.newValue"
											@click="addValue(filter.id)"
										/>
									</div>
								</div>

								<!-- Advanced Config (Collapsible) -->
								<transition name="slide-down">
									<div v-if="filter.showConfig && showFiltersConfig" class="filter-config">
										<vs-select
											v-model="filter.group"
											:placeholder="$t('FiltersAdvanced.GroupPlaceholder')"
											size="small"
											class="w-full"
										>
											<vs-select-item value="none" :text="$t('FiltersAdvanced.NoGroup')" />
											<vs-select-item value="and" :text="$t('FiltersAdvanced.AndGroup')" />
											<vs-select-item value="or" :text="$t('FiltersAdvanced.OrGroup')" />
										</vs-select>
									</div>
								</transition>
							</div>
						</transition-group>

						<!-- Empty State -->
						<div v-if="filtersList.length === 0" class="empty-filters">
							<feather-icon icon="FilterIcon" svgClasses="w-12 h-12 text-grey" class="mb-2" />
							<p class="text-grey text-sm">{{ $t("FiltersAdvanced.NoFiltersAdded") }}</p>
							<p class="text-grey text-xs">{{ $t("FiltersAdvanced.SelectFieldAbove") }}</p>
						</div>
					</div>

					<!-- Filter Configuration -->
					<div class="filter-configuration">
						<label>{{ $t("FiltersAdvanced.FilterConfiguration") }}:</label>

						<div class="config-grid">
							<!-- Logical Operator -->
							<div class="config-item">
								<label class="config-label">{{ $t("FiltersAdvanced.LogicalOperator") }}</label>
								<vs-select v-model="logicalOperator" class="w-full">
									<vs-select-item value="and" :text="$t('FiltersAdvanced.AND')" />
									<vs-select-item value="or" :text="$t('FiltersAdvanced.OR')" />
								</vs-select>
							</div>

							<!-- Match Type -->
							<div class="config-item">
								<label class="config-label">{{ $t("FiltersAdvanced.MatchType") }}</label>
								<vs-select v-model="matchFilterSelect" class="w-full">
									<vs-select-item :value="false" :text="$t('FiltersAdvanced.PartialMatch')" />
									<vs-select-item :value="true" :text="$t('FiltersAdvanced.ExactMatch')" />
								</vs-select>
							</div>

							<!-- Sort Configuration -->
							<div class="config-item">
								<label class="config-label">{{ $t("FiltersAdvanced.Sorting") }}</label>
								<div class="sort-config">
									<vs-select v-model="sortField" class="flex-1">
										<vs-select-item
											v-for="field in availableFields"
											:key="field.value"
											:value="field.value"
											:text="field.label"
										/>
									</vs-select>
									<div class="sort-direction">
										<vs-button
											:color="sortDirection === 'asc' ? 'primary' : ''"
											:type="sortDirection === 'asc' ? 'filled' : 'border'"
											size="small"
											icon-pack="feather"
											icon="icon-arrow-up"
											@click="sortDirection = 'asc'"
										/>
										<vs-button
											:color="sortDirection === 'desc' ? 'primary' : ''"
											:type="sortDirection === 'desc' ? 'filled' : 'border'"
											size="small"
											icon-pack="feather"
											icon="icon-arrow-down"
											@click="sortDirection = 'desc'"
										/>
									</div>
								</div>
							</div>
						</div>
					</div>

					<!-- Apply Button (Advanced Mode) -->
					<div class="advanced-actions">
						<vs-button
							color="success"
							class="flex-1"
							icon-pack="feather"
							icon="icon-check"
							:disabled="isLoading || filtersList.length === 0"
							@click="applyFilters"
						>
							{{ $t("FiltersAdvanced.ApplyFilters") }}
						</vs-button>
					</div>
				</div>

				<!-- Error Display -->
				<transition name="fade">
					<div v-if="errorMessage" class="error-alert">
						<vs-alert color="danger" active="true" icon-pack="feather" icon="icon-alert-circle">
							{{ errorMessage }}
						</vs-alert>
					</div>
				</transition>

				<!-- Loading Overlay -->
				<transition name="fade">
					<div v-if="isLoading" class="loading-overlay">
						<vs-progress indeterminate color="primary"></vs-progress>
						<p class="loading-text">{{ $t("FiltersAdvanced.Searching") }}...</p>
					</div>
				</transition>
			</div>
		</vx-card>
	</div>
</template>

<script>
import axios from "@/axios";

export default {
	name: "DynamicFilterPanel",
	props: {
		title: {
			type: String,
			default: null,
		},
		endpoint: {
			type: String,
			default: null,
		},
		defaultFilters: {
			type: Object,
			default: function () {
				return {};
			},
		},
		showFiltersConfig: {
			type: Boolean,
			default: false,
		},
		autoSearch: {
			type: Boolean,
			default: false,
		},
		onResponse: {
			type: Function,
			default: null,
		},
		showCloseButton: {
			type: Boolean,
			default: false,
		},
		searchConfig: {
			type: Object,
			default: function () {
				return {
					fields: ["id", "name"],
					page: 1,
					quantity: 10,
					order_sort: {id: "asc"},
				};
			},
		},
		// Available fields for filtering
		availableFields: {
			type: Array,
			default: function () {
				return [
					{value: "id", label: "ID"},
					{value: "name", label: "Nombre"},
					{value: "company_id", label: "ID Empresa"},
					{value: "success", label: "Éxito"},
					{value: "status", label: "Estado"},
					{value: "added_on", label: "Fecha Creación"},
					{value: "total_points", label: "Total Puntos"},
					{value: "total_clusters", label: "Total Clusters"},
					{value: "total_micro_clusters", label: "Total Micro Clusters"},
					{value: "folder_path", label: "Ruta Carpeta"},
					{value: "update_on", label: "Fecha Actualización"},
				];
			},
		},
	},
	data() {
		return {
			isAdvancedMode: false,
			matchFilter: false,
			matchFilterSelect: false,
			logicalOperator: "and",
			filtersList: [],
			isLoading: false,
			errorMessage: "",
			filterIdCounter: 0,

			// Basic Mode
			basicFilter: {
				field: "",
				value: "",
			},

			// Advanced Mode
			selectedFieldToAdd: "",
			sortField: "id",
			sortDirection: "asc",
		};
	},
	computed: {
		/**
		 * Available fields that haven't been added yet
		 */
		availableFieldsToAdd() {
			const addedFields = this.filtersList.map(function (f) {
				return f.field;
			});
			return this.availableFields.filter(function (f) {
				return !addedFields.includes(f.value);
			});
		},

		/**
		 * Build the filters object with ARRAYS (API requirement)
		 */
		filtersObject() {
			const filters = {};
			this.filtersList.forEach(function (filter) {
				if (filter.field && filter.values.length > 0) {
					filters[filter.field] = filter.values;
				}
			});
			return filters;
		},

		/**
		 * Build the filters_config object (advanced grouping)
		 */
		filtersConfigObject() {
			if (!this.showFiltersConfig) {
				return {};
			}

			const config = {
				and: [],
				or: [],
			};

			this.filtersList.forEach(function (filter) {
				if (filter.field && filter.values.length > 0) {
					if (filter.group === "and") {
						config.and.push(filter.field);
					} else if (filter.group === "or") {
						config.or.push(filter.field);
					}
				}
			});

			return config;
		},

		/**
		 * Build the complete request body
		 */
		requestBody() {
			const body = Object.assign({}, this.searchConfig, {
				match_filter: this.matchFilter,
				logical_operator: this.logicalOperator,
				filters: this.filtersObject,
				order_sort: {},
			});

			// Set order_sort
			body.order_sort[this.sortField] = this.sortDirection;

			// Only add filters_config if showFiltersConfig is enabled
			if (this.showFiltersConfig) {
				body.filters_config = this.filtersConfigObject;
			}

			return body;
		},
	},
	watch: {
		matchFilterSelect: function (val) {
			this.matchFilter = val;
		},
		filtersList: {
			handler: function () {
				if (this.autoSearch && this.filtersList.length > 0) {
					clearTimeout(this.searchTimeout);
					this.searchTimeout = setTimeout(
						function () {
							this.applyFilters();
						}.bind(this),
						500
					);
				}
			},
			deep: true,
		},
	},
	mounted() {
		this.initializeFilters();
	},
	methods: {
		/**
		 * Initialize filters from defaultFilters prop
		 */
		initializeFilters() {
			if (this.defaultFilters && Object.keys(this.defaultFilters).length > 0) {
				Object.entries(this.defaultFilters).forEach(
					function (entry) {
						const field = entry[0];
						const value = entry[1];
						const values = Array.isArray(value) ? value : [value];
						this.filtersList.push({
							id: this.filterIdCounter++,
							field: field,
							values: values,
							newValue: "",
							group: "none",
							showConfig: false,
						});
					}.bind(this)
				);
			}
		},

		/**
		 * Get field label from value
		 */
		getFieldLabel(fieldValue) {
			const field = this.availableFields.find(function (f) {
				return f.value === fieldValue;
			});
			return field ? field.label : fieldValue;
		},

		/**
		 * Add filter from dropdown (Advanced Mode)
		 */
		addFilterFromDropdown() {
			if (!this.selectedFieldToAdd) return;

			this.filtersList.push({
				id: this.filterIdCounter++,
				field: this.selectedFieldToAdd,
				values: [],
				newValue: "",
				group: "none",
				showConfig: false,
			});

			this.selectedFieldToAdd = "";
		},

		/**
		 * Remove a filter
		 */
		removeFilter(filterId) {
			const index = this.filtersList.findIndex(function (f) {
				return f.id === filterId;
			});
			if (index !== -1) {
				this.filtersList.splice(index, 1);
			}
		},

		/**
		 * Add value to filter
		 */
		addValue(filterId) {
			const filter = this.filtersList.find(function (f) {
				return f.id === filterId;
			});
			if (filter && filter.newValue && filter.newValue.trim()) {
				filter.values.push(filter.newValue.trim());
				filter.newValue = "";
			}
		},

		/**
		 * Remove value from filter
		 */
		removeValue(filterId, valueIndex) {
			const filter = this.filtersList.find(function (f) {
				return f.id === filterId;
			});
			if (filter) {
				filter.values.splice(valueIndex, 1);
			}
		},

		/**
		 * Toggle filter configuration
		 */
		toggleFilterConfig(filterId) {
			const filter = this.filtersList.find(function (f) {
				return f.id === filterId;
			});
			if (filter) {
				filter.showConfig = !filter.showConfig;
			}
		},

		/**
		 * Apply basic filter
		 */
		applyBasicFilter() {
			if (!this.basicFilter.field || !this.basicFilter.value) {
				this.errorMessage = this.$t("FiltersAdvanced.InvalidFilters");
				return;
			}

			// Clear existing filters
			this.filtersList = [];

			// Add basic filter
			this.filtersList.push({
				id: this.filterIdCounter++,
				field: this.basicFilter.field,
				values: [this.basicFilter.value],
				newValue: "",
				group: "none",
				showConfig: false,
			});

			// Apply
			this.applyFilters();
		},

		/**
		 * Clear basic value
		 */
		clearBasicValue() {
			this.basicFilter.value = "";
		},

		/**
		 * Clear all filters and reset to default state
		 */
		clearFilters() {
			this.filtersList = [];
			this.matchFilter = false;
			this.matchFilterSelect = false;
			this.logicalOperator = "and";
			this.errorMessage = "";
			this.basicFilter = {
				field: "",
				value: "",
			};
			this.selectedFieldToAdd = "";

			this.$emit("clear");

			if (this.autoSearch) {
				this.applyFilters();
			}
		},

		/**
		 * Apply filters and execute search
		 */
		applyFilters() {
			this.errorMessage = "";

			// Validate
			const validFilters = this.filtersList.filter(function (f) {
				return f.field && f.values.length > 0;
			});

			if (validFilters.length === 0 && this.filtersList.length > 0) {
				this.errorMessage = this.$t("FiltersAdvanced.InvalidFilters");
				return;
			}

			const body = this.requestBody;

			// If endpoint is provided, make the API call
			if (this.endpoint) {
				this.executeSearch(body);
			} else {
				// Otherwise, just emit the body
				this.$emit("applyFilters", body);
			}
		},

		/**
		 * Execute the search request to the API
		 */
		executeSearch(body) {
			this.isLoading = true;

			axios
				.post(this.endpoint, body)
				.then(
					function (response) {
						if (this.onResponse && typeof this.onResponse === "function") {
							this.onResponse(response.data);
						}

						this.$emit("onSearch", response.data);

						this.$vs.notify({
							title: this.$t("Success"),
							text: this.$t("FiltersAdvanced.SearchSuccess"),
							color: "success",
							icon: "check_circle",
						});
					}.bind(this)
				)
				.catch(
					function (error) {
						this.errorMessage =
							(error.response && error.response.data && error.response.data.message) ||
							this.$t("FiltersAdvanced.SearchError");

						this.$vs.notify({
							title: this.$t("Error"),
							text: this.errorMessage,
							color: "danger",
							icon: "error",
						});

						this.$emit("error", error);
					}.bind(this)
				)
				.finally(
					function () {
						this.isLoading = false;
					}.bind(this)
				);
		},

		/**
		 * Export current filters configuration
		 */
		exportFilters() {
			return {
				matchFilter: this.matchFilter,
				logicalOperator: this.logicalOperator,
				filters: this.filtersObject,
				filtersConfig: this.filtersConfigObject,
				orderSort: {},
			};
		},

		/**
		 * Import filters configuration
		 */
		importFilters(config) {
			this.clearFilters();

			if (config.matchFilter !== undefined) {
				this.matchFilter = config.matchFilter;
				this.matchFilterSelect = config.matchFilter;
			}

			if (config.logicalOperator) {
				this.logicalOperator = config.logicalOperator;
			}

			if (config.filters) {
				Object.entries(config.filters).forEach(
					function (entry) {
						const field = entry[0];
						const values = entry[1];
						const filter = {
							id: this.filterIdCounter++,
							field: field,
							values: Array.isArray(values) ? values : [values],
							newValue: "",
							group: "none",
							showConfig: false,
						};

						if (config.filtersConfig) {
							if (config.filtersConfig.and && config.filtersConfig.and.includes(field)) {
								filter.group = "and";
							} else if (config.filtersConfig.or && config.filtersConfig.or.includes(field)) {
								filter.group = "or";
							}
						}

						this.filtersList.push(filter);
					}.bind(this)
				);
			}
		},
	},
};
</script>

<style lang="scss" scoped>
.dynamic-filter-panel {
	// Header
	.filter-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 1.25rem 1.5rem;
		border-bottom: 1px solid rgba(0, 0, 0, 0.08);
		background: linear-gradient(to bottom, #ffffff, #f9fafb);

		.header-content {
			display: flex;
			align-items: center;
			gap: 2rem;
			flex: 1;

			.filter-title {
				font-size: 1.125rem;
				font-weight: 600;
				margin: 0;
				color: #2c3e50;
			}

			.mode-toggle {
				display: flex;
				align-items: center;
				gap: 0.75rem;
				padding: 0.5rem 1rem;
				background: white;
				border-radius: 8px;
				border: 1px solid rgba(0, 0, 0, 0.1);

				.mode-label {
					font-size: 0.875rem;
					color: #666;
					font-weight: 500;
				}
			}
		}
	}

	// Content
	.filter-content {
		position: relative;
		padding: 1.5rem;
		max-height: calc(100vh - 250px);
		overflow-y: auto;

		&::-webkit-scrollbar {
			width: 6px;
		}

		&::-webkit-scrollbar-track {
			background: #f1f1f1;
			border-radius: 10px;
		}

		&::-webkit-scrollbar-thumb {
			background: #888;
			border-radius: 10px;

			&:hover {
				background: #555;
			}
		}
	}

	// Mode Descriptions
	.mode-description {
		margin-bottom: 1.5rem;
		padding: 1rem;
		background: linear-gradient(135deg, #667eea15 0%, #764ba215 100%);
		border-radius: 8px;
		border-left: 3px solid #667eea;

		h6 {
			font-size: 1rem;
			font-weight: 600;
			margin: 0 0 0.5rem 0;
			color: #667eea;
		}

		p {
			margin: 0;
			font-size: 0.875rem;
			color: #666;
			line-height: 1.5;
		}
	}

	// BASIC MODE
	.basic-mode {
		.basic-filter-row {
			display: grid;
			grid-template-columns: 1fr 1fr;
			gap: 1rem;
			margin-bottom: 1.5rem;

			.filter-field,
			.filter-value {
				label {
					display: block;
					font-size: 0.875rem;
					font-weight: 600;
					color: #555;
					margin-bottom: 0.5rem;
				}
			}

			.value-input-wrapper {
				display: flex;
				gap: 0.5rem;
				align-items: center;
			}
		}

		.basic-actions {
			display: flex;
			gap: 1rem;
		}
	}

	// ADVANCED MODE
	.advanced-mode {
		.field-selection {
			margin-bottom: 1.5rem;

			label {
				display: block;
				font-size: 0.875rem;
				font-weight: 600;
				color: #555;
				margin-bottom: 0.75rem;
			}

			.selection-row {
				display: flex;
				gap: 0.75rem;
			}
		}

		.active-filters-section {
			margin-bottom: 1.5rem;

			> label {
				display: block;
				font-size: 0.875rem;
				font-weight: 600;
				color: #555;
				margin-bottom: 0.75rem;
			}

			.filters-container {
				display: flex;
				flex-direction: column;
				gap: 1rem;
			}

			.filter-item-advanced {
				background: white;
				border: 1px solid rgba(0, 0, 0, 0.1);
				border-radius: 12px;
				padding: 1rem;
				transition: all 0.3s ease;

				&:hover {
					box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
					border-color: #667eea;
				}

				.filter-item-header {
					display: flex;
					justify-content: space-between;
					align-items: center;
					margin-bottom: 0.75rem;

					.filter-label {
						font-size: 0.875rem;
						font-weight: 600;
						color: #2c3e50;
					}

					.filter-actions {
						display: flex;
						gap: 0.25rem;
					}
				}

				.filter-values {
					.chips-container {
						margin-bottom: 0.75rem;

						.chips-wrapper {
							display: flex;
							flex-wrap: wrap;
							gap: 0.5rem;
						}

						.chip {
							display: inline-flex;
							align-items: center;
							gap: 0.5rem;
							padding: 0.375rem 0.75rem;
							background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
							color: white;
							border-radius: 20px;
							font-size: 0.8125rem;
							font-weight: 500;
							transition: all 0.2s ease;
							animation: chipIn 0.3s ease;

							&:hover {
								transform: translateY(-2px);
								box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
							}

							.chip-text {
								max-width: 150px;
								overflow: hidden;
								text-overflow: ellipsis;
								white-space: nowrap;
							}
						}
					}

					.add-value-input {
						display: flex;
						gap: 0.5rem;
						align-items: center;
					}
				}

				.filter-config {
					margin-top: 0.75rem;
					padding-top: 0.75rem;
					border-top: 1px solid rgba(0, 0, 0, 0.06);
				}
			}
		}

		.filter-configuration {
			padding: 1.25rem;
			background: #f9fafb;
			border-radius: 12px;
			margin-bottom: 1.5rem;

			> label {
				display: block;
				font-size: 0.875rem;
				font-weight: 600;
				color: #555;
				margin-bottom: 1rem;
			}

			.config-grid {
				display: grid;
				grid-template-columns: repeat(3, 1fr);
				gap: 1rem;

				.config-item {
					.config-label {
						display: block;
						font-size: 0.75rem;
						font-weight: 600;
						color: #777;
						margin-bottom: 0.5rem;
						text-transform: uppercase;
						letter-spacing: 0.5px;
					}

					.sort-config {
						display: flex;
						gap: 0.5rem;

						.sort-direction {
							display: flex;
							gap: 0.25rem;
						}
					}
				}
			}
		}

		.advanced-actions {
			display: flex;
			justify-content: stretch;
		}
	}

	// Empty State
	.empty-filters {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 3rem 1rem;
		background: #f9fafb;
		border-radius: 12px;
		border: 2px dashed rgba(0, 0, 0, 0.1);
		text-align: center;
	}

	// Error Alert
	.error-alert {
		margin-top: 1rem;
	}

	// Loading Overlay
	.loading-overlay {
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(255, 255, 255, 0.95);
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		z-index: 100;
		border-radius: 8px;
		backdrop-filter: blur(4px);

		.loading-text {
			margin-top: 1rem;
			color: #667eea;
			font-weight: 500;
		}
	}
}

// Animations
@keyframes chipIn {
	from {
		opacity: 0;
		transform: scale(0.8);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

.filter-list-enter-active,
.filter-list-leave-active {
	transition: all 0.3s ease;
}

.filter-list-enter,
.filter-list-leave-to {
	opacity: 0;
	transform: translateX(-20px);
}

.chip-enter-active,
.chip-leave-active {
	transition: all 0.2s ease;
}

.chip-enter,
.chip-leave-to {
	opacity: 0;
	transform: scale(0.8);
}

.slide-down-enter-active,
.slide-down-leave-active {
	transition: all 0.3s ease;
}

.slide-down-enter,
.slide-down-leave-to {
	max-height: 0;
	opacity: 0;
	overflow: hidden;
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
	opacity: 0;
}

// Responsive
@media screen and (max-width: 768px) {
	.dynamic-filter-panel {
		.filter-header {
			flex-direction: column;
			gap: 1rem;
			align-items: flex-start;

			.header-content {
				flex-direction: column;
				gap: 1rem;
				align-items: flex-start;
			}
		}

		.basic-mode .basic-filter-row {
			grid-template-columns: 1fr;
		}

		.advanced-mode {
			.field-selection .selection-row {
				flex-direction: column;
			}

			.filter-configuration .config-grid {
				grid-template-columns: 1fr;
			}
		}
	}
}
</style>
